import React, { useCallback, useEffect, useState } from 'react';
import { Landmark, TrendingUp, BarChart3, Loader2, Sparkles } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * LiquidityHorizonPanel — Sprint 173 Part B.
 *
 * Public-markets benchmarking + modelled path-to-NASDAQ for the founder.
 * Mounted at the top of the NASDAQ tab inside SecureVault. Cream cards
 * (#FAFAED) + deep plum (#2A2235) per the established design language.
 */

const PALETTE = {
  cream: '#FAFAED',
  plum: '#2A2235',
  muted: '#5C526A',
  coral: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  border: 'rgba(92,82,106,0.10)',
};

const fmtMoney = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${Number(n).toLocaleString()}`;
};

const fmtUsers = (n) => {
  if (n === null || n === undefined) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return Number(n).toLocaleString();
};

export const LiquidityHorizonPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try { setData(await apiClient.request('/api/vault/nasdaq/liquidity-horizon')); }
    catch (e) { setErr(e.message || 'Failed'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <div className="rounded-2xl p-5 text-sm" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, color: PALETTE.muted }} data-testid="liquidity-horizon-loading">
        <Loader2 size={14} className="inline animate-spin mr-2" /> Loading liquidity horizon…
      </div>
    );
  }
  if (err || !data) return <div className="text-xs" style={{ color: '#7A4040' }} data-testid="liquidity-horizon-error">{err || '—'}</div>;

  const path = data.path_to_listing || [];
  const live = data.live || {};
  const stats = data.comp_stats || {};
  const maxEV = Math.max(1, ...path.map(p => p.enterprise_valuation_usd));

  return (
    <div className="rounded-2xl p-5 lg:p-6" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}`, borderLeft: `4px solid ${PALETTE.lavender}` }} data-testid="liquidity-horizon-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1" style={{ color: '#3F4D7A' }}>Liquidity Horizon</div>
          <h3 className="text-lg font-black" style={{ color: PALETTE.plum }}>Public-markets benchmarking · path to NASDAQ</h3>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.muted }}>{data.narrative}</p>
        </div>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-[0.12em]" style={{ background: data.data_source === 'live' ? PALETTE.mint : '#E9E4F2', color: data.data_source === 'live' ? '#1F5C44' : PALETTE.muted }} data-testid="liquidity-horizon-source">
          {data.data_source === 'live' ? 'Live market data' : 'Founder bench (Q4 ’25)'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile label="Live ARR (implied)" value={fmtMoney(live.implied_annual_revenue_usd)} sub={`${fmtUsers(live.users)} × $${live.monthly_per_user_usd}/mo × 12`} testid="liquidity-arr" accent="#9A4A33" />
        <Tile label="Current Enterprise Valuation" value={fmtMoney(live.current_enterprise_valuation_usd)} sub="from dynamic engine" testid="liquidity-current-ev" accent="#3F4D7A" />
        <Tile label="Comp-Blend Valuation" value={fmtMoney(live.median_comp_blend_valuation_usd)} sub={`Median ${stats.median_ev_revenue || 0}× EV/Rev (n=${stats.count || 0})`} testid="liquidity-blend" accent="#1F5C44" />
        <Tile label="Founder Multiple" value={`${live.founder_multiple || 12}x`} sub="ARR multiplier in engine" testid="liquidity-founder-mult" accent={PALETTE.plum} />
      </div>

      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-2" style={{ color: PALETTE.muted }}>Modelled path to listing</div>
        <ol className="grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="liquidity-path">
          {path.map(step => {
            const heightPct = Math.max(8, (step.enterprise_valuation_usd / maxEV) * 100);
            return (
              <li key={step.step} className="rounded-xl p-3 flex flex-col" style={{ background: '#fff', border: `1px solid ${PALETTE.border}` }} data-testid={`liquidity-step-${step.step}`}>
                <div className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: PALETTE.muted }}>Step {step.step}</div>
                <div className="text-sm font-black" style={{ color: PALETTE.plum }}>{step.label}</div>
                <div className="text-[10px] mt-0.5" style={{ color: PALETTE.muted }}>{fmtUsers(step.users)} users · {step.ev_multiple}× EV/Rev</div>
                <div className="text-lg font-black mt-1.5 tabular-nums" style={{ color: '#3F4D7A' }} data-testid={`liquidity-step-ev-${step.step}`}>{fmtMoney(step.enterprise_valuation_usd)}</div>
                <div className="text-[10px]" style={{ color: PALETTE.muted }}>ARR {fmtMoney(step.annual_revenue_usd)} · T+{step.horizon_months}mo</div>
                <div className="h-1.5 mt-2 rounded-full overflow-hidden" style={{ background: '#F0EDF5' }}>
                  <div className="h-full" style={{ width: `${heightPct}%`, background: PALETTE.lavender }} />
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] font-bold mb-2" style={{ color: PALETTE.muted }}>Public-tech comp set</div>
        <div className="overflow-x-auto" data-testid="liquidity-comps">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ color: PALETTE.muted }}>
                <th className="text-left font-bold uppercase tracking-[0.08em] py-1.5">Ticker</th>
                <th className="text-left font-bold uppercase tracking-[0.08em] py-1.5">Vertical</th>
                <th className="text-right font-bold uppercase tracking-[0.08em] py-1.5">EV / Rev</th>
                <th className="text-right font-bold uppercase tracking-[0.08em] py-1.5">Growth</th>
                <th className="text-right font-bold uppercase tracking-[0.08em] py-1.5">Rule of 40</th>
              </tr>
            </thead>
            <tbody>
              {(data.public_comps || []).map(c => (
                <tr key={c.ticker} style={{ borderTop: `1px solid ${PALETTE.border}` }} data-testid={`liquidity-comp-${c.ticker}`}>
                  <td className="py-1.5 font-black" style={{ color: PALETTE.plum }}>{c.ticker}</td>
                  <td className="py-1.5" style={{ color: PALETTE.plum }}>{c.name} <span className="text-[10px]" style={{ color: PALETTE.muted }}>· {c.vertical}</span></td>
                  <td className="py-1.5 text-right font-bold tabular-nums" style={{ color: PALETTE.plum }}>{c.ev_revenue_multiple}×</td>
                  <td className="py-1.5 text-right tabular-nums" style={{ color: c.growth_pct < 0 ? '#7A4040' : '#1F5C44' }}>{c.growth_pct >= 0 ? '+' : ''}{c.growth_pct}%</td>
                  <td className="py-1.5 text-right tabular-nums" style={{ color: PALETTE.plum }}>{c.rule_of_40}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Tile = ({ label, value, sub, testid, accent }) => (
  <div className="rounded-xl p-3" style={{ background: '#fff', border: `1px solid ${PALETTE.border}` }} data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.14em] font-bold" style={{ color: PALETTE.muted }}>{label}</div>
    <div className="text-xl font-black tabular-nums mt-0.5" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-[10px] mt-0.5" style={{ color: PALETTE.muted }}>{sub}</div>}
  </div>
);

export default LiquidityHorizonPanel;
